"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useCallback, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useRefresh3D } from "./Refresh3DContext.js";

export default function Header() {
  const router = useRouter();
  const { refresh } = useRefresh3D() || { refresh: () => console.warn("Refresh3DContext not available") };
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredDropdown, setHoveredDropdown] = useState(null);
  const [isNavHovered, setIsNavHovered] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [dropdownTimeout, setDropdownTimeout] = useState(null);

  // Detect mobile view based on window width
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768); // Matches Tailwind's md breakpoint
    };
    handleResize(); // Initial check
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle logo click with navigation and refresh
  const handleLogoClick = useCallback(
    async (e) => {
      e.preventDefault();
      setIsRefreshing(true);
      try {
        await router.push("/");
        refresh();
      } catch (error) {
        console.error("Navigation or refresh failed:", error);
      } finally {
        setIsRefreshing(false);
      }
    },
    [router, refresh]
  );

  // Close mobile menu on route change
  useEffect(() => {
    const handleRouteChange = () => setIsMenuOpen(false);
    router.events?.on("routeChangeComplete", handleRouteChange);
    return () => router.events?.off("routeChangeComplete", handleRouteChange);
  }, [router]);

  // Toggle dropdown on click for both mobile and web views
  const toggleDropdown = () => {
    setHoveredDropdown(hoveredDropdown === "whatWeDo" ? null : "whatWeDo");
  };

  // Handle menu item click to close mobile menu and dropdown
  const handleMenuItemClick = () => {
    setIsMenuOpen(false);
    setHoveredDropdown(null);
    refresh();
  };

  // Hover handlers for web view with delay
  const handleMouseEnter = () => {
    if (!isMobile) {
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout);
        setDropdownTimeout(null);
      }
      setHoveredDropdown("whatWeDo");
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      const timeout = setTimeout(() => {
        setHoveredDropdown(null);
      }, 300); // .5 second delay
      setDropdownTimeout(timeout);
    }
  };

  // Focus handlers for web view (accessibility)
  const handleFocus = () => {
    if (!isMobile) {
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout);
        setDropdownTimeout(null);
      }
      setHoveredDropdown("whatWeDo");
    }
  };

  const handleBlur = () => {
    if (!isMobile) {
      const timeout = setTimeout(() => {
        setHoveredDropdown(null);
      }, 300); // 2 second delay
      setDropdownTimeout(timeout);
    }
  };

  // Dropdown content hover handlers with delay
  const handleDropdownMouseEnter = () => {
    if (!isMobile) {
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout);
        setDropdownTimeout(null);
      }
      setHoveredDropdown("whatWeDo");
    }
  };

  const handleDropdownMouseLeave = () => {
    if (!isMobile) {
      const timeout = setTimeout(() => {
        setHoveredDropdown(null);
      }, 300); // 2 second delay
      setDropdownTimeout(timeout);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout);
      }
    };
  }, [dropdownTimeout]);

  return (
    <header className="text-white px-4 sm:px-6 md:px-8 flex flex-wrap justify-between items-center fixed w-full z-[1000] bg-transparent">
      {/* Logo Section */}
      <div className="flex items-center justify-between w-full md:w-auto py-2">
        <Link href="/" className="flex items-center group relative h-12 w-auto" onClick={handleLogoClick}>
          <Image
            src="/Expo-logo-removebg-preview.png"
            alt="Exponent Solutions"
            width={60}
            height={30}
            sizes="(max-width: 768px) 60px, 80px"
            className={`object-contain transition-all duration-500 group-hover:scale-10 md:group-hover:animate-[spin_0.0s_linear_0] ${isRefreshing ? "" : ""}`}  
            onError={() => console.error("Failed to load logo image: /Expo-logo-removebg-preview.png")}
            priority
          />
          <div className="absolute inset-x-0 h-10 to-transparent opacity-0 md:group-hover:opacity-100 transition-opacity duration-300 transform scale-y-[-1]"></div>
        </Link>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 text-white focus:outline-none rounded"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMenuOpen}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <div
        className={`${isMenuOpen ? "flex" : "hidden"} md:flex flex-col md:flex-row w-full md:w-auto mb-2 md:mb-1 transition-all duration-300 ease-in-out`}
        onMouseEnter={() => setIsNavHovered(true)}
        onMouseLeave={() => setIsNavHovered(false)}
      >
        <nav
          className={`relative px-4 sm:px-6 py-2 font-medium text-white bg-gradient-to-r from-black/90 to-black/90 rounded-lg space-y-3 md:space-y-0 md:space-x-4 flex flex-col md:flex-row items-start md:items-center transition-all duration-500 ${isNavHovered ? "shadow-[0_0_15px_5px_rgba(255,20,147,0.4)]" : ""}`}
        >
          {/* What We Do Dropdown */}
          <div
            className="relative w-full md:w-auto group"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            <div
              className=" transition-colors cursor-pointer flex items-center justify-center py-2 focus:outline-none  rounded w-full md:w-auto"
              onClick={toggleDropdown}
              tabIndex={0}
              aria-haspopup="true"
              aria-expanded={hoveredDropdown === "whatWeDo"}
            > What We Do
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {/* Dropdown Menu */}
            <div
              className={`w-full md:absolute md:left-0 md:top-full md:mt-2 md:w-44 bg-black rounded-lg shadow-sm z-[1001] border border-gray-800 transition-all duration-300 ${hoveredDropdown === "whatWeDo" ? "block opacity-100 visible translate-y-0" : "hidden opacity-0 invisible translate-y-2 md:translate-y-2"}`}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleDropdownMouseLeave}
            >
              <ul className="py-2 text-sm text-white">
                <li>
                  <Link
                    href="/our-capabilities"
                    onClick={handleMenuItemClick}
                    className="block px-4 py-2 hover:bg-gray-800 hover:text-blue-300 focus:bg-gray-800 focus:text-blue-300 focus:outline-none"
                  > Our Capabilities
                  </Link>
                </li>
                <li>
                  <Link
                    href="/our-products"
                    onClick={handleMenuItemClick}
                    className="block px-4 py-2 hover:bg-gray-800 hover:text-blue-300 focus:bg-gray-800 focus:text-blue-300 focus:outline-none"
                  > Our Products
                  </Link>
                </li>
                <li>
                  <Link
                    href="/Services"
                    onClick={handleMenuItemClick}
                    className="block px-4 py-2 hover:bg-gray-800 hover:text-blue-300 focus:bg-gray-800 focus:text-blue-300 focus:outline-none"
                  > Services
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <Link
            href="/who-we-are"
            onClick={handleMenuItemClick}
            className="hover:text-blue-300 py-2 rounded"
          >
            Who We Are
          </Link>
          <Link
            href="/careers"
            onClick={handleMenuItemClick}
            className="hover:text-blue-300 py-2 rounded"
          >
            Career
          </Link>
        </nav>
      </div>

      {/* Button */}
      <div
        className={`${isMenuOpen ? "flex" : "hidden"} md:flex w-full md:w-auto mb-4 md:mb-2 p-2 transition-all duration-300 ease-in-out`}
      >
        <Link href="/contact" onClick={handleMenuItemClick}>
          <button className="w-full md:w-auto px-6 py-3 font-medium text-white bg-gradient-to-r from-black to-gray-900 rounded-lg overflow-hidden group transition-all duration-500 hover:shadow-lg hover:shadow-indigo-500/80 focus:outline-none focus:ring-2 focus:ring-indigo-500">
            Contact Us
          </button>
        </Link>
      </div>
    </header>
  );
}